// src/components/admin/StaffLeaveManager.jsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../utils/firebase';

// Utility function to generate UUID for leave records
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// submittedByRole: role of the user saving the leave ('owner','admin','manager' = auto-approved; 'staff' = pending)
// onConflictResolved(leaveDocId): optional callback — called after a conflict is resolved here so
//   the parent (Admin.jsx) can dismiss the matching alert from the notifications modal.
function StaffLeaveManager({ salonId, staffId, staffName, leaveAllocation: initialLeaveAllocation, onUpdate, submittedByRole, onConflictResolved }) {
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [allStaffs, setAllStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [leaveAllocation, setLeaveAllocation] = useState(initialLeaveAllocation || { annualLeave: 20, medicalLeave: 10 });
  const [editingAllocation, setEditingAllocation] = useState(false);
  const [allocationForm, setAllocationForm] = useState({ annualLeave: 20, medicalLeave: 10 });
  const [savingAllocation, setSavingAllocation] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'annual', // 'annual', 'medical', or 'timeoff'
    startDate: '',
    endDate: '',
    timeType: 'full', // 'full', 'am', 'pm', 'custom' (for time off)
    startTime: '09:00', // For custom time off
    endTime: '10:00', // For custom time off
    reason: '',
    notes: ''
  });
  
  const [leaveSummary, setLeaveSummary] = useState({
    annualLeaveUsed: 0,
    medicalLeaveUsed: 0,
    timeOffHours: 0,
    annualLeaveRemaining: 0,
    medicalLeaveRemaining: 0
  });

  // Conflict resolution state
  const [resolvingLeaveId, setResolvingLeaveId] = useState(null);
  const [resolutionMap, setResolutionMap] = useState({}); // { apptId: { action: 'reassign'|'cancel', newStaffId, newStaffName } }
  const [resolutionReason, setResolutionReason] = useState('');
  const [resolvingSaving, setResolvingSaving] = useState(false);
  const [staffPerAppt, setStaffPerAppt] = useState({}); // { apptId: [staffList] }

  // ── Real-time leave records listener ─────────────────────────────────────────
  // Using onSnapshot so any change from the notifications modal (conflictsResolved)
  // or from anywhere else is immediately reflected here without a manual reload.
  useEffect(() => {
    if (!staffId || !salonId) return;
    setLoading(true);
    const leaveQuery = query(
      collection(db, 'leave'),
      where('staffId', '==', staffId),
      where('salonId', '==', salonId)
    );
    const unsub = onSnapshot(leaveQuery, snap => {
      const records = [];
      snap.forEach(d => {
        const data = d.data();
        if (data.startDate?.seconds) {
          const startDate = new Date(data.startDate.seconds * 1000);
          if (startDate.getFullYear() === selectedYear) {
            records.push({ id: d.id, leaveId: data.leaveId || d.id, ...data });
          }
        }
      });
      records.sort((a, b) => {
        const dateA = new Date((a.startDate?.seconds || 0) * 1000);
        const dateB = new Date((b.startDate?.seconds || 0) * 1000);
        return dateB - dateA;
      });
      setLeaveRecords(records);
      setError('');
      setLoading(false);
    }, err => {
      console.error('Error loading leave data:', err);
      setError('Failed to load leave records: ' + err.message);
      setLoading(false);
    });
    return () => unsub();
  }, [staffId, salonId, selectedYear]);

  useEffect(() => {
    if (staffId && salonId) loadAllStaffs();
  }, [staffId, salonId]);

  // ── Auto-resolve stale conflicts ─────────────────────────────────────────────
  // The conflictingAppointments[] array stored on a leave doc is a snapshot from
  // when the leave was created. If those appointments were later reassigned or
  // cancelled (via the notifications modal or by the customer directly), the
  // leave doc's conflictsResolved flag is never updated automatically.
  //
  // This effect runs whenever leaveRecords changes. For every leave record that
  // still shows unresolved conflicts, it fetches the live status of each
  // stored conflicting appointment. If ALL of them are now cancelled or
  // assigned to a different staff member, the leave doc is auto-marked resolved.
  useEffect(() => {
    const unresolvedRecords = leaveRecords.filter(
      r => r.hasConflicts && !r.conflictsResolved && (r.conflictingAppointments || []).length > 0
    );
    if (unresolvedRecords.length === 0) return;

    let cancelled = false; // guard against running after component unmounts
    (async () => {
      for (const record of unresolvedRecords) {
        if (cancelled) break;
        const appts = record.conflictingAppointments || [];
        let allGone = true;

        for (const appt of appts) {
          if (cancelled) break;
          try {
            const snap = await getDoc(doc(db, 'appointments', appt.id));
            if (!snap.exists()) continue; // deleted — no longer a conflict

            const data = snap.data();
            // Still a live conflict: status is active AND still assigned to this staff
            const stillConflicts =
              ['approved', 'pending'].includes(data.status) &&
              (data.staffId === staffId || data.staffId === record.staffId);

            if (stillConflicts) {
              allGone = false;
              break;
            }
          } catch {
            // If we can't read the doc, leave it as-is
            allGone = false;
            break;
          }
        }

        if (allGone && !cancelled) {
          try {
            await updateDoc(doc(db, 'leave', record.id), {
              conflictsResolved:   true,
              conflictsResolvedAt: serverTimestamp(),
              conflictsResolvedBy: 'auto',
            });
            // Also dismiss from the notifications modal if a callback was provided
            if (onConflictResolved) onConflictResolved(record.id);
          } catch (err) {
            console.error('Auto-resolve leave conflict failed:', err);
          }
        }
      }
    })();

    return () => { cancelled = true; };
  }, [leaveRecords, staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    calculateLeaveSummary();
  }, [leaveRecords, leaveAllocation]);



  const loadAllStaffs = async () => {
    try {
      console.log('Loading all staffs for salonId:', salonId);
      
      // Get ALL documents from staffs collection first to avoid index issues
      const staffsRef = collection(db, 'staff');
      const staffsSnapshot = await getDocs(staffsRef);
      
      console.log('Total staffs documents found for all staffs:', staffsSnapshot.size);
      
      const staffsData = [];
      staffsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('Processing staff document:', doc.id, 'salonId in doc:', data.salonId);
        
        // Filter by salonId on client side and exclude current staff
        if (data.salonId === salonId && (data.staffId !== staffId)) {
          console.log('Found other staff in salon:', data.name);
          staffsData.push({
            id: doc.id,
            staffId: data.staffId || doc.id,
            name: data.name
          });
        }
      });
      
      console.log('Loaded other staffs for salon:', staffsData);
      setAllStaffs(staffsData);
    } catch (err) {
      console.error('Error loading staffs:', err);
    }
  };

  const handleEditAllocation = () => {
    setAllocationForm({
      annualLeave: leaveAllocation?.annualLeave || 20,
      medicalLeave: leaveAllocation?.medicalLeave || 10
    });
    setEditingAllocation(true);
  };

  const handleCancelAllocationEdit = () => {
    setEditingAllocation(false);
  };

  const handleSaveAllocation = async () => {
    try {
      setSavingAllocation(true);
      setError('');

      const newAllocation = {
        annualLeave: parseInt(allocationForm.annualLeave) || 0,
        medicalLeave: parseInt(allocationForm.medicalLeave) || 0
      };

      // Find the staff document and update leaveAllocation
      const staffRef = collection(db, 'staff');
      const staffSnapshot = await getDocs(staffRef);
      let staffDocId = null;

      staffSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        if ((data.staffId === staffId || docSnap.id === staffId) && data.salonId === salonId) {
          staffDocId = docSnap.id;
        }
      });

      if (staffDocId) {
        const staffDocRef = doc(db, 'staff', staffDocId);
        await updateDoc(staffDocRef, { leaveAllocation: newAllocation });
        setLeaveAllocation(newAllocation);
        setEditingAllocation(false);
      } else {
        setError('Staff record not found. Unable to update leave allocation.');
      }
    } catch (err) {
      console.error('Error saving leave allocation:', err);
      setError('Failed to save leave allocation: ' + err.message);
    } finally {
      setSavingAllocation(false);
    }
  };

  const calculateLeaveSummary = () => {
    let annualUsed = 0;
    let medicalUsed = 0;
    let timeOffHours = 0;

    leaveRecords.forEach(record => {
      if (record.status !== 'approved') return;
      if (record.type === 'annual') {
        annualUsed += calculateLeaveDays(record);
      } else if (record.type === 'medical') {
        medicalUsed += calculateLeaveDays(record);
      } else if (record.type === 'timeoff') {
        timeOffHours += calculateTimeOffHours(record);
      }
    });
    
    setLeaveSummary({
      annualLeaveUsed: annualUsed,
      medicalLeaveUsed: medicalUsed,
      timeOffHours: timeOffHours,
      annualLeaveRemaining: (leaveAllocation?.annualLeave || 0) - annualUsed,
      medicalLeaveRemaining: (leaveAllocation?.medicalLeave || 0) - medicalUsed
    });
  };

  const calculateLeaveDays = (leave) => {
    if (!leave.startDate || !leave.endDate) return 0;

    const start = new Date(leave.startDate.seconds * 1000);
    const end = new Date(leave.endDate.seconds * 1000);

    let days = 0;
    const current = new Date(start);

    while (current <= end) {
      if (leave.timeType === 'full') {
        days += 1;
      } else if (leave.timeType === 'am' || leave.timeType === 'pm') {
        days += 0.5;
      }
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const calculateTimeOffHours = (leave) => {
    if (leave.type !== 'timeoff') return 0;
    if (!leave.startDate || !leave.endDate) return 0;

    const start = new Date(leave.startDate.seconds * 1000);
    const end = new Date(leave.endDate.seconds * 1000);

    let totalHours = 0;
    const current = new Date(start);

    while (current <= end) {
      if (leave.timeType === 'custom' && leave.startTime && leave.endTime) {
        const [startHour, startMin] = leave.startTime.split(':').map(Number);
        const [endHour, endMin] = leave.endTime.split(':').map(Number);
        totalHours += ((endHour * 60 + endMin) - (startHour * 60 + startMin)) / 60;
      } else if (leave.timeType === 'am') {
        totalHours += 4; // 9AM to 1PM
      } else if (leave.timeType === 'pm') {
        totalHours += 4; // 1PM to 5PM
      } else if (leave.timeType === 'full') {
        totalHours += 8;
      }
      current.setDate(current.getDate() + 1);
    }

    return Math.round(totalHours * 100) / 100;
  };

  // Check for appointment conflicts during the requested leave period.
  // Returns full appointment objects for conflict tracking and admin resolution.
  const checkAppointmentConflicts = async (startDateStr, endDateStr) => {
    const leaveStart = new Date(startDateStr); leaveStart.setHours(0, 0, 0, 0);
    const leaveEnd   = new Date(endDateStr);   leaveEnd.setHours(23, 59, 59, 999);
    try {
      const apptRef  = collection(db, 'appointments');
      const apptSnap = await getDocs(apptRef);
      const conflicts = [];
      apptSnap.forEach(d => {
        const data = d.data();
        if (data.salonId !== salonId || data.staffId !== staffId) return;
        if (data.status !== 'approved' && data.status !== 'pending') return;
        const apptStart = data.dateTimeRequested?.toDate?.()
          ?? data.startDateTime?.toDate?.()
          ?? new Date(data.dateTimeRequested || data.startDateTime);
        if (!apptStart || isNaN(apptStart.getTime())) return;
        const apptDay = new Date(apptStart); apptDay.setHours(0, 0, 0, 0);
        if (apptDay >= leaveStart && apptDay <= leaveEnd) {
          conflicts.push({
            id: d.id,
            customerName: data.customerName || data.customerEmail || 'Customer',
            customerEmail: data.customerEmail || '',
            serviceName: data.serviceRequested?.name || data.serviceName || 'Service',
            dateTime: data.dateTimeRequested || data.startDateTime || null,
            locationId: data.locationId || null,
          });
        }
      });
      return conflicts;
    } catch (err) {
      console.error('Error checking appointment conflicts:', err);
      return [];
    }
  };

  const checkForConflicts = async (newLeave) => {
    const conflicts = [];

    // Returns the [startMinutes, endMinutes] window for a leave record.
    // AM = 9:00–13:00, PM = 13:00–17:00, custom = actual times, full/other = whole day.
    const getLeaveWindow = (leave) => {
      if (leave.timeType === 'am') return [540, 780];
      if (leave.timeType === 'pm') return [780, 1020];
      if (leave.timeType === 'custom' && leave.startTime && leave.endTime)
        return [timeToMinutes(leave.startTime), timeToMinutes(leave.endTime)];
      return [0, 1440];
    };

    // Check for self-conflicts
    const selfConflicts = leaveRecords.filter(record => {
      if (!record.startDate || !record.endDate) return false;

      const recordStart = new Date(record.startDate.seconds * 1000);
      const recordEnd   = new Date(record.endDate.seconds * 1000);
      const newStart    = new Date(newLeave.startDate);
      const newEnd      = new Date(newLeave.endDate);

      if (newStart > recordEnd || newEnd < recordStart) return false;

      // For two single-day leaves on the exact same date, compare time windows
      // so that complementary half-days (am + pm) are not treated as conflicts.
      const recordSingleDay = recordStart.toDateString() === recordEnd.toDateString();
      const newSingleDay    = newStart.toDateString() === newEnd.toDateString();
      if (recordSingleDay && newSingleDay && newStart.toDateString() === recordStart.toDateString()) {
        const [s1, e1] = getLeaveWindow(newLeave);
        const [s2, e2] = getLeaveWindow(record);
        return s1 < e2 && e1 > s2;
      }

      return true;
    });

    if (selfConflicts.length > 0) {
      conflicts.push(`Conflicts with existing leave: ${selfConflicts.map(c =>
        formatDate(new Date(c.startDate.seconds * 1000)) + ' - ' +
        formatDate(new Date(c.endDate.seconds * 1000))
      ).join(', ')}`);
    }

    return conflicts;
  };

  const timeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let newFormData = {
      ...formData,
      [name]: value
    };
    
    // Reset timeType when leave type changes
    if (name === 'type') {
      if (value === 'timeoff') {
        newFormData.timeType = 'custom';
        newFormData.startTime = '09:00';
        newFormData.endTime = '10:00';
      } else {
        newFormData.timeType = 'full';
      }
    }
    
    // Ensure end time is after start time for custom time off
    if (name === 'startTime' && formData.type === 'timeoff' && formData.timeType === 'custom') {
      const startMinutes = timeToMinutes(value);
      const endMinutes = timeToMinutes(formData.endTime);
      if (endMinutes <= startMinutes) {
        const newEndHour = Math.floor(startMinutes / 60) + 1;
        const newEndTime = `${newEndHour.toString().padStart(2, '0')}:00`;
        newFormData.endTime = newEndTime;
      }
    }
    
    setFormData(newFormData);
  };

  const handleAddLeave = () => {
    setShowAddForm(true);
    setFormData({
      type: 'annual',
      startDate: '',
      endDate: '',
      timeType: 'full',
      startTime: '09:00',
      endTime: '10:00',
      reason: '',
      notes: ''
    });
    setError('');
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setFormData({
      type: 'annual',
      startDate: '',
      endDate: '',
      timeType: 'full',
      startTime: '09:00',
      endTime: '10:00',
      reason: '',
      notes: ''
    });
    setError('');
  };

  const sendLeaveNotifications = async (leaveData) => {
    try {
      // Query salonUsers for admins with matching salonId
      const usersRef = collection(db, 'salonUsers');
      const usersSnapshot = await getDocs(usersRef);

      const admins = [];
      usersSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.salonId === salonId && ['owner', 'admin', 'manager'].includes(data.role)) {
          admins.push(data);
        }
      });

      const startDateStr = new Date(leaveData.startDate.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const endDateStr = new Date(leaveData.endDate.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const leaveTypeLabel = leaveData.type === 'annual' ? 'Annual Leave' : leaveData.type === 'medical' ? 'Medical Leave' : 'Time Off';

      for (const admin of admins) {
        // Send email notification
        if (admin.email) {
          try {
            await addDoc(collection(db, 'mail'), {
              to: admin.email,
              message: {
                subject: `Leave Request: ${staffName} - ${leaveTypeLabel}`,
                html: `<p><strong>${staffName}</strong> has a new <strong>${leaveTypeLabel}</strong> record.</p>
                       <p><strong>Period:</strong> ${startDateStr} - ${endDateStr}</p>
                       <p><strong>Reason:</strong> ${leaveData.reason}</p>`
              },
              salonId: salonId,
              createdAt: serverTimestamp()
            });
          } catch (emailErr) {
            console.error('Failed to send leave email notification:', emailErr);
          }
        }

        // Send push notification
        if (admin.fcmToken) {
          try {
            await addDoc(collection(db, 'pushNotifications'), {
              token: admin.fcmToken,
              title: `Leave: ${staffName}`,
              body: `${leaveTypeLabel} from ${startDateStr} to ${endDateStr}`,
              salonId: salonId,
              createdAt: serverTimestamp()
            });
          } catch (pushErr) {
            console.error('Failed to send leave push notification:', pushErr);
          }
        }
      }
    } catch (err) {
      console.error('Error sending leave notifications:', err);
    }
  };

  const handleSaveLeave = async () => {
    try {
      console.log('Starting to save leave record...');
      console.log('Form data:', formData);
      
      // Validation
      if (!formData.startDate || !formData.endDate) {
        setError('Start date and end date are required');
        return;
      }

      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const today = new Date();
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(today.getFullYear() + 1);

      if (startDate > endDate) {
        setError('End date must be after start date');
        return;
      }

      if (endDate > oneYearFromNow) {
        setError('Leave cannot be scheduled more than 1 year in advance');
        return;
      }

      if (!formData.reason.trim()) {
        setError('Reason is required');
        return;
      }

      // Additional validation for custom time off
      if (formData.type === 'timeoff' && formData.timeType === 'custom') {
        if (!formData.startTime || !formData.endTime) {
          setError('Start time and end time are required for custom time off');
          return;
        }
        
        const startMinutes = timeToMinutes(formData.startTime);
        const endMinutes = timeToMinutes(formData.endTime);
        
        if (endMinutes <= startMinutes) {
          setError('End time must be after start time');
          return;
        }
        
        if ((endMinutes - startMinutes) < 60) {
          setError('Minimum time off duration is 1 hour');
          return;
        }
      }

      // Check for existing leave conflicts
      const conflicts = await checkForConflicts(formData);
      if (conflicts.length > 0) {
        setError(conflicts.join('\n'));
        return;
      }

      // Check for appointment conflicts — no longer blocks; stored for admin resolution.
      const apptConflicts = await checkAppointmentConflicts(formData.startDate, formData.endDate);

      // Check leave balance for annual and medical leave only (not time off)
      if (formData.type === 'annual') {
        const leaveDays = calculateLeaveDays({
          startDate: Timestamp.fromDate(startDate),
          endDate: Timestamp.fromDate(endDate),
          timeType: formData.timeType
        });
        
        if (leaveSummary.annualLeaveRemaining < leaveDays) {
          setError(`Insufficient annual leave balance. Remaining: ${leaveSummary.annualLeaveRemaining} days, Requested: ${leaveDays} days`);
          return;
        }
      } else if (formData.type === 'medical') {
        const leaveDays = calculateLeaveDays({
          startDate: Timestamp.fromDate(startDate),
          endDate: Timestamp.fromDate(endDate),
          timeType: formData.timeType
        });
        
        if (leaveSummary.medicalLeaveRemaining < leaveDays) {
          setError(`Insufficient medical leave balance. Remaining: ${leaveSummary.medicalLeaveRemaining} days, Requested: ${leaveDays} days`);
          return;
        }
      }

      setLoading(true);
      console.log('Validation passed, creating leave data...');

      // Generate a unique leave ID
      const leaveId = generateUUID();

      // Admin always auto-approves regardless of leave type.
      // Staff-submitted Time Off stays pending (handled in StaffPortal, not here).
      const createdByValue = submittedByRole || 'admin';

      // Save to Firebase - main 'leave' collection
      const leaveData = {
        leaveId:                 leaveId,
        type:                    formData.type,
        startDate:               Timestamp.fromDate(startDate),
        endDate:                 Timestamp.fromDate(endDate),
        timeType:                formData.timeType,
        reason:                  formData.reason.trim(),
        notes:                   formData.notes.trim(),
        staffId:                 staffId,
        salonId:                 salonId,
        status:                  'approved',
        createdAt:               Timestamp.fromDate(new Date()),
        updatedAt:               Timestamp.fromDate(new Date()),
        createdBy:               createdByValue,
        approvedBy:              'admin',
        approvedAt:              Timestamp.fromDate(new Date()),
        hasConflicts:            apptConflicts.length > 0,
        conflictsResolved:       apptConflicts.length === 0,
        conflictingAppointments: apptConflicts,
      };

      // Add time fields for custom time off
      if (formData.type === 'timeoff' && formData.timeType === 'custom') {
        leaveData.startTime = formData.startTime;
        leaveData.endTime = formData.endTime;
      }

      console.log('Leave data to save:', leaveData);
      console.log('Saving to main leave collection');

      const leaveRef = collection(db, 'leave');
      const docRef = await addDoc(leaveRef, leaveData);

      console.log('Leave record saved with ID:', docRef.id);

      // Send notifications to admins (non-blocking)
      sendLeaveNotifications(leaveData);

      setShowAddForm(false);
      setError('');
      
      if (onUpdate) {
        onUpdate();
      }

    } catch (err) {
      console.error('Error saving leave:', err);
      setError('Failed to save leave record: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeave = async (leaveDocumentId) => {
    if (window.confirm('Are you sure you want to delete this leave record?')) {
      try {
        setLoading(true);
        
        // Delete from main 'leave' collection using document ID
        const leaveDocRef = doc(db, 'leave', leaveDocumentId);
        await deleteDoc(leaveDocRef);
        // onSnapshot listener will automatically refresh leaveRecords

        if (onUpdate) {
          onUpdate();
        }
      } catch (err) {
        console.error('Error deleting leave:', err);
        setError('Failed to delete leave record');
      } finally {
        setLoading(false);
      }
    }
  };

  // ── Conflict resolution handlers ─────────────────────────────────────────────

  const openResolvePanel = async (leaveRecord) => {
    setResolvingLeaveId(leaveRecord.id);
    setResolutionMap({});
    setResolutionReason('');

    // For each conflicting appointment load available staff (same location if known).
    const staffMap = {};
    for (const appt of (leaveRecord.conflictingAppointments || [])) {
      if (appt.locationId) {
        try {
          const q = query(collection(db, 'workingHours'),
            where('salonId', '==', salonId),
            where('locationId', '==', appt.locationId)
          );
          const snap = await getDocs(q);
          const staffIdsAtLocation = new Set();
          snap.forEach(d => staffIdsAtLocation.add(d.data().staffId));
          staffMap[appt.id] = allStaffs.filter(s => staffIdsAtLocation.has(s.staffId));
          if (staffMap[appt.id].length === 0) staffMap[appt.id] = allStaffs;
        } catch {
          staffMap[appt.id] = allStaffs;
        }
      } else {
        staffMap[appt.id] = allStaffs;
      }
    }
    setStaffPerAppt(staffMap);
  };

  const closeResolvePanel = () => {
    setResolvingLeaveId(null);
    setResolutionMap({});
    setResolutionReason('');
  };

  const handleResolutionChange = (apptId, field, value) => {
    setResolutionMap(prev => ({
      ...prev,
      [apptId]: { ...(prev[apptId] || {}), [field]: value },
    }));
  };

  const submitResolution = async (leaveRecord) => {
    if (!resolutionReason.trim()) {
      setError('Reason for reassignment/cancellation is required.');
      return;
    }
    const appointments = leaveRecord.conflictingAppointments || [];
    const unresolved = appointments.filter(a => !resolutionMap[a.id]?.action);
    if (unresolved.length > 0) {
      setError('Please select an action (reassign or cancel) for every appointment.');
      return;
    }
    // Validate reassign actions have a staff selected
    const missingStaff = appointments.filter(
      a => resolutionMap[a.id]?.action === 'reassign' && !resolutionMap[a.id]?.newStaffId
    );
    if (missingStaff.length > 0) {
      setError('Please select a replacement staff member for each reassigned appointment.');
      return;
    }

    setResolvingSaving(true);
    setError('');
    try {
      for (const appt of appointments) {
        const resolution = resolutionMap[appt.id];
        const apptRef = doc(db, 'appointments', appt.id);
        if (resolution.action === 'reassign') {
          await updateDoc(apptRef, {
            staffId:                   resolution.newStaffId,
            staffName:                 resolution.newStaffName,
            previousStaffId:           leaveRecord.staffId,
            previousStaffName:         leaveRecord.staffName || staffName,
            staffReassigned:           true,
            staffReassignmentReason:   resolutionReason.trim(),
            updatedAt:                 serverTimestamp(),
          });
        } else if (resolution.action === 'cancel') {
          await updateDoc(apptRef, {
            status:             'cancelled',
            cancellationReason: `Staff availability: ${resolutionReason.trim()}`,
            updatedAt:          serverTimestamp(),
          });
        }
      }
      // Mark leave record conflicts as resolved
      const leaveDocRef = doc(db, 'leave', leaveRecord.id);
      await updateDoc(leaveDocRef, {
        conflictsResolved:    true,
        conflictsResolvedAt:  serverTimestamp(),
        conflictsResolvedBy:  'admin',
      });
      // Notify Admin.jsx to dismiss this alert from the notifications modal
      if (onConflictResolved) onConflictResolved(leaveRecord.id);
      closeResolvePanel();
      // onSnapshot listener refreshes leaveRecords automatically
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Failed to resolve conflicts: ' + err.message);
    } finally {
      setResolvingSaving(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTimeType = (record) => {
    switch (record.timeType) {
      case 'full': return 'Full Day';
      case 'am': return 'Morning (9AM-1PM)';
      case 'pm': return 'Afternoon (1PM-End)';
      case 'custom': 
        if (record.type === 'timeoff' && record.startTime && record.endTime) {
          return `Custom (${formatTime12Hour(record.startTime)}-${formatTime12Hour(record.endTime)})`;
        }
        return 'Custom Time';
      default: return record.timeType;
    }
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
  };

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case 'annual': return '#4CAF50';
      case 'medical': return '#FF9800';
      case 'timeoff': return '#2196F3';
      default: return '#757575';
    }
  };

  const getLeaveTypeLabel = (type) => {
    switch (type) {
      case 'annual': return 'Annual';
      case 'medical': return 'Medical';
      case 'timeoff': return 'Time Off';
      default: return type;
    }
  };

  // Generate hour options for time selectors
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 60) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    return options;
  };

  if (loading && leaveRecords.length === 0) {
    return <div className="loading">Loading leave records...</div>;
  }

  return (
    <div className="leave-management-container">
      {error && <div className="error-message">{error}</div>}
      
      {/* SIMPLIFIED Leave Summary Section - Single box */}
      <div className="leave-summary-single-box">
        <h3>Leave Summary for {staffName} ({selectedYear})</h3>
        
        <div className="leave-header">
          {/* Year Selector */}
          <div className="year-selector">
            <label>Year: </label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>{year}</option>
                );
              })}
            </select>
          </div>
        </div>
        
        <div className="leave-summary-stats">
          <div className="leave-stat-card">
            <h4>Annual Leave</h4>
            <div className="leave-stat-number">{leaveSummary.annualLeaveUsed}</div>
            <div className="leave-stat-label">of {leaveAllocation?.annualLeave || 0} days used</div>
            <small>({leaveSummary.annualLeaveRemaining} remaining)</small>
          </div>

          <div className="leave-stat-card">
            <h4>Medical Leave</h4>
            <div className="leave-stat-number">{leaveSummary.medicalLeaveUsed}</div>
            <div className="leave-stat-label">of {leaveAllocation?.medicalLeave || 0} days used</div>
            <small>({leaveSummary.medicalLeaveRemaining} remaining)</small>
          </div>

          <div className="leave-stat-card">
            <h4>Time Off</h4>
            <div className="leave-stat-number">{leaveSummary.timeOffHours}</div>
            <div className="leave-stat-label">hours taken (no limit)</div>
          </div>
        </div>

        {/* Leave Allocation Editor */}
        {!editingAllocation ? (
          <div className="leave-allocation-actions">
            <button className="edit-button" onClick={handleEditAllocation}>
              Edit Leave Allocation
            </button>
          </div>
        ) : (
          <div className="leave-allocation-form">
            <h4>Edit Leave Allocation</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Annual Leave (days per year)</label>
                <input
                  type="number"
                  value={allocationForm.annualLeave}
                  onChange={(e) => setAllocationForm({ ...allocationForm, annualLeave: e.target.value })}
                  min="0"
                  max="365"
                  placeholder="20"
                />
              </div>
              <div className="form-group">
                <label>Medical Leave (days per year)</label>
                <input
                  type="number"
                  value={allocationForm.medicalLeave}
                  onChange={(e) => setAllocationForm({ ...allocationForm, medicalLeave: e.target.value })}
                  min="0"
                  max="365"
                  placeholder="10"
                />
              </div>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancelAllocationEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-button"
                onClick={handleSaveAllocation}
                disabled={savingAllocation}
              >
                {savingAllocation ? 'Saving...' : 'Save Allocation'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Leave Records Section */}
      <div className="leave-records-section">
        <h3>Leave Records</h3>
        
        {/* Add Leave Button */}
        {!showAddForm && (
          <div className="leave-actions">
            <button className="add-button" onClick={handleAddLeave}>
              Add Leave Record
            </button>
          </div>
        )}

        {/* Add Leave Form */}
        {showAddForm && (
          <div className="leave-form">
            <h4>Add New Leave Record</h4>

            <div className="form-row">
              <div className="form-group">
                <label>Leave Type <span style={{ color: '#E64C31' }}>*</span></label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="annual">Annual Leave</option>
                  <option value="medical">Medical Leave</option>
                  <option value="timeoff">Time Off</option>
                </select>
              </div>

              <div className="form-group">
                <label>Time Type <span style={{ color: '#E64C31' }}>*</span></label>
                <select
                  name="timeType"
                  value={formData.timeType}
                  onChange={handleInputChange}
                >
                  {formData.type === 'timeoff' ? (
                    <>
                      <option value="custom">Custom Time (1+ hours)</option>
                      <option value="am">Morning (9AM-1PM)</option>
                      <option value="pm">Afternoon (1PM-End)</option>
                      <option value="full">Full Day</option>
                    </>
                  ) : (
                    <>
                      <option value="full">Full Day</option>
                      <option value="am">Morning (9AM-1PM)</option>
                      <option value="pm">Afternoon (1PM-End)</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Custom Time Selectors for Time Off */}
            {formData.type === 'timeoff' && formData.timeType === 'custom' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time <span style={{ color: '#E64C31' }}>*</span></label>
                  <select
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                  >
                    {generateTimeOptions().map(time => (
                      <option key={`start-${time}`} value={time}>
                        {formatTime12Hour(time)}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>End Time <span style={{ color: '#E64C31' }}>*</span></label>
                  <select
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                  >
                    {generateTimeOptions().filter(time => timeToMinutes(time) > timeToMinutes(formData.startTime)).map(time => (
                      <option key={`end-${time}`} value={time}>
                        {formatTime12Hour(time)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            
            <div className="form-row">
              <div className="form-group">
                <label>Start Date <span style={{ color: '#E64C31' }}>*</span></label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label>End Date <span style={{ color: '#E64C31' }}>*</span></label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  min={formData.startDate || new Date().toISOString().split('T')[0]}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Reason <span style={{ color: '#E64C31' }}>*</span></label>
              <input
                type="text"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                placeholder={
                  formData.type === 'timeoff' 
                    ? "e.g. Personal appointment, Extended lunch, etc."
                    : "e.g. Family vacation, Medical procedure, etc."
                }
                required
              />
            </div>
            
            <div className="form-group">
              <label>Additional Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                placeholder="Any additional information..."
              />
            </div>

            <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px', fontFamily: 'inherit' }}>
              Fields marked with <span style={{ color: '#E64C31' }}>*</span> are mandatory.
            </p>

            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={handleCancelAdd}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="save-button"
                onClick={handleSaveLeave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Leave Record'}
              </button>
            </div>
          </div>
        )}

        {/* Leave Records Display */}
        {leaveRecords.length === 0 ? (
          <div className="no-leave-records">
            No leave records found for {selectedYear}.
          </div>
        ) : (
          <div className="records-list">
            {leaveRecords.map(record => (
              <div key={record.id} className="leave-record-item">
                <div className="record-header">
                  <div className="record-type">
                    <span 
                      className={`leave-type-badge leave-type-${record.type}`}
                      style={{ backgroundColor: getLeaveTypeColor(record.type) }}
                    >
                      {getLeaveTypeLabel(record.type)}
                    </span>
                    <span className="time-type">{formatTimeType(record)}</span>
                  </div>
                  <div className="record-dates">
                    {record.startDate && record.endDate ? (
                      <>
                        {formatDate(new Date(record.startDate.seconds * 1000))} - {formatDate(new Date(record.endDate.seconds * 1000))}
                        <small>
                          {record.type === 'timeoff' 
                            ? `(${calculateTimeOffHours(record)} hours)`
                            : `(${calculateLeaveDays(record)} days)`
                          }
                        </small>
                      </>
                    ) : (
                      <span>Invalid date</span>
                    )}
                  </div>
                </div>
                
                <div className="record-content">
                  <p><strong>Reason:</strong> {record.reason}</p>
                  {record.notes && <p><strong>Notes:</strong> {record.notes}</p>}
                  {record.status && (
                    <p><strong>Status:</strong>
                      <span className={`status-badge status-${record.status}`}>
                        {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                      </span>
                    </p>
                  )}
                  {record.hasConflicts && (
                    <p>
                      <strong>Appointment Conflicts:</strong>{' '}
                      {record.conflictsResolved
                        ? <span style={{ color: '#155724', fontWeight: 600 }}>✓ Resolved</span>
                        : <span style={{ color: '#856404', fontWeight: 600 }}>⚠ {(record.conflictingAppointments || []).length} appointment(s) need reassignment</span>
                      }
                    </p>
                  )}
                  {record.createdAt && (
                    <small>Created: {formatDate(new Date(record.createdAt.seconds * 1000))}</small>
                  )}
                </div>

                <div className="record-actions">
                  {record.hasConflicts && !record.conflictsResolved && (
                    <button
                      className="save-button small"
                      style={{ backgroundColor: '#E64C31', marginRight: '8px' }}
                      onClick={() => resolvingLeaveId === record.id ? closeResolvePanel() : openResolvePanel(record)}
                    >
                      {resolvingLeaveId === record.id ? 'Cancel Resolve' : 'Resolve Conflicts'}
                    </button>
                  )}
                  <button
                    className="remove-button small"
                    onClick={() => handleDeleteLeave(record.id)}
                  >
                    Delete
                  </button>
                </div>

                {/* ── Inline conflict resolution panel ── */}
                {resolvingLeaveId === record.id && (
                  <div style={{
                    margin: '12px 0 0', padding: '16px', background: '#fff8f0',
                    border: '1px solid #f0c080', borderRadius: '8px',
                  }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#7a4000' }}>
                      Resolve Appointment Conflicts
                    </h4>
                    <p style={{ fontSize: '13px', color: '#555', margin: '0 0 12px' }}>
                      For each appointment below, either reassign it to another available staff member or cancel it.
                      A notification will be sent to the customer with the reason you provide.
                    </p>

                    {/* Mandatory reason field */}
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                        Reason for reassignment / cancellation <span style={{ color: '#E64C31' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={resolutionReason}
                        onChange={e => setResolutionReason(e.target.value)}
                        placeholder="e.g. Staff is on approved leave"
                        style={{
                          width: '100%', padding: '8px 10px', border: '1px solid #ddd',
                          borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    {/* Per-appointment rows */}
                    {(record.conflictingAppointments || []).map(appt => {
                      const apptDate = appt.dateTime
                        ? (appt.dateTime?.toDate ? appt.dateTime.toDate() : new Date(appt.dateTime))
                        : null;
                      const dateLabel = apptDate && !isNaN(apptDate)
                        ? apptDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                        : '—';
                      const timeLabel = apptDate && !isNaN(apptDate)
                        ? apptDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                        : '';
                      const resolution = resolutionMap[appt.id] || {};
                      const availableStaff = staffPerAppt[appt.id] || allStaffs;

                      return (
                        <div key={appt.id} style={{
                          background: '#fff', border: '1px solid #e8d8c0', borderRadius: '6px',
                          padding: '12px', marginBottom: '10px',
                        }}>
                          <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                            <strong>{appt.customerName}</strong> — {appt.serviceName}
                            <span style={{ color: '#888', marginLeft: '8px' }}>{dateLabel}{timeLabel ? ` at ${timeLabel}` : ''}</span>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                              value={resolution.action || ''}
                              onChange={e => handleResolutionChange(appt.id, 'action', e.target.value)}
                              style={{
                                padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px',
                                fontSize: '13px', background: '#fff',
                              }}
                            >
                              <option value="">— Select action —</option>
                              <option value="reassign">Reassign to another staff</option>
                              <option value="cancel">Cancel appointment</option>
                            </select>

                            {resolution.action === 'reassign' && (
                              availableStaff.length > 0 ? (
                                <select
                                  value={resolution.newStaffId || ''}
                                  onChange={e => {
                                    const selected = availableStaff.find(s => s.staffId === e.target.value);
                                    handleResolutionChange(appt.id, 'newStaffId', e.target.value);
                                    handleResolutionChange(appt.id, 'newStaffName', selected?.name || '');
                                  }}
                                  style={{
                                    padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px',
                                    fontSize: '13px', background: '#fff',
                                  }}
                                >
                                  <option value="">— Select staff —</option>
                                  {availableStaff.map(s => (
                                    <option key={s.staffId} value={s.staffId}>{s.name}</option>
                                  ))}
                                </select>
                              ) : (
                                <span style={{ fontSize: '12px', color: '#856404', fontStyle: 'italic' }}>
                                  ⚠ No other staff available at this location. Consider cancelling.
                                </span>
                              )
                            )}

                            {resolution.action === 'cancel' && (
                              <span style={{ fontSize: '12px', color: '#721c24' }}>
                                Customer will be notified of the cancellation with the reason above.
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {error && (
                      <p style={{ color: '#721c24', fontSize: '13px', margin: '8px 0' }}>{error}</p>
                    )}

                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button
                        className="save-button"
                        onClick={() => submitResolution(record)}
                        disabled={resolvingSaving}
                        style={{ fontSize: '13px', padding: '8px 16px' }}
                      >
                        {resolvingSaving ? 'Saving…' : 'Submit Resolution'}
                      </button>
                      <button
                        className="cancel-button"
                        onClick={closeResolvePanel}
                        style={{ fontSize: '13px', padding: '8px 16px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffLeaveManager;